import { InfiniteTable, InfiniteTableColumns, Stack, Text } from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import React, { useCallback, useMemo, useState } from 'react';
import { NavLink, useParams } from 'react-router';

import { Company, CompanyGrantRelationship } from '../../company';
import { ConfirmButton } from '../../components/confirm_button';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { CreateAllVehicleGrantsMutationVariables, RevokeAllVehicleGrantsMutationVariables } from '../../lib/graphql';
import { customSlugify } from '../../lib/util/url';
import { Routes } from '../../routes';

export interface VehicleGrantListDataProps {
  data: {
    companyRelationships: CompanyGrantRelationship[];
    company_id: string;
    company: Company;
  };
}

interface VehicleGrantListProps {
  createVehicleGrants: (input: CreateAllVehicleGrantsMutationVariables['input']) => Promise<void>;
  revokeVehicleGrants: (input: RevokeAllVehicleGrantsMutationVariables['input']) => Promise<void>;
}

interface GrantRow extends CompanyGrantRelationship {
  id: string;
}

export const VehicleGrantList: React.FC<VehicleGrantListProps & VehicleGrantListDataProps> = ({
  data,
  createVehicleGrants,
  revokeVehicleGrants,
}) => {
  const { slug_company_name } = useParams<{ slug_company_name?: string }>();
  const { companyRelationships, company } = data;
  const isNotAdmin = !useIsAdmin();
  const hasCompanyVehicleGrantPermission = useHasScopes([BackofficeScope.companyWriteVehicles]);
  const isPrivilegedUser = !isNotAdmin || hasCompanyVehicleGrantPermission;

  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    let el: HTMLElement | null = node.parentElement;
    while (el !== null) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
        setScrollParent(el);
        return;
      }
      el = el.parentElement;
    }
  }, []);

  const tableData = useMemo(
    () => (companyRelationships ?? []).map((r) => ({ ...r, id: r.companyRelationshipId })),
    [companyRelationships],
  );

  const columns: InfiniteTableColumns<GrantRow> = useMemo(
    () => [
      {
        id: 'targetCompany',
        title: 'Company',
        width: '30%',
        minWidth: '150px',
        cellContent: ({ row }) => (
          <NavLink
            to={Routes.CompanyVehicleGrants.generatePath({
              company_id: row.targetCompany.company_id,
              slug_company_name: customSlugify(row.targetCompany.company_name),
            })}>
            {row.targetCompany.company_name}
          </NavLink>
        ),
      },
      {
        id: 'sourceCompany',
        title: 'Vehicles',
        width: '20%',
        minWidth: '80px',
        cellContent: ({ row }) => <>{row.sourceCompany.vehicles?.all?.count ?? 0}</>,
      },
      {
        id: 'companyRelationshipId',
        title: 'Granted vehicles',
        width: '20%',
        minWidth: '100px',
        cellContent: ({ row }) => <>{row.targetCompany.vehicleGrantPartner?.grantCount ?? 0}</>,
      },
      {
        id: 'id',
        title: 'Actions',
        width: '30%',
        minWidth: '200px',
        cellContent: ({ row }) => (
          <Stack direction="row" gap="small">
            <ConfirmButton
              label="Grant all"
              disabled={!isPrivilegedUser}
              initialButtonColor="green"
              onConfirm={async () =>
                await createVehicleGrants({
                  grantedByCompany: row.sourceCompany.company_id,
                  grantedToCompany: row.targetCompany.company_id,
                })
              }
            />
            <ConfirmButton
              label="Revoke all"
              disabled={!isPrivilegedUser}
              initialButtonColor="red"
              onConfirm={async () =>
                await revokeVehicleGrants({
                  revokedByCompany: row.sourceCompany.company_id,
                  revokedFromCompany: row.targetCompany.company_id,
                })
              }
            />
          </Stack>
        ),
      },
    ],
    [isPrivilegedUser, createVehicleGrants, revokeVehicleGrants],
  );

  return (
    <Stack direction="column" gap="medium">
      <h2 style={{ margin: 0 }}>Vehicle Grants</h2>
      <p style={{ margin: 0 }}>
        This table contains all companies that <strong>{company.company_name}</strong> is a subcarrier of and can grant
        vehicles to.
        <br /> To add companies to this list,{' '}
        <NavLink
          to={Routes.CompanyRelationshipCreate.generatePath({ company_id: company.company_id, slug_company_name })}>
          add a subcarrier relationship between {company.company_name} and that company
        </NavLink>
      </p>

      {tableData.length === 0 ? (
        <Text>No subcarrier relationships defined for this company</Text>
      ) : (
        <div className="sub-table-scroll-container" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div ref={containerRefCallback}>
            {scrollParent !== null && (
              <InfiniteTable<GrantRow>
                columns={columns}
                data={tableData}
                scrollParent={scrollParent}
                className="compact-table"
              />
            )}
          </div>
        </div>
      )}
    </Stack>
  );
};
